import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Lock, RefreshCw, LogOut, User } from "lucide-react";
import { useDocuments, useTrustConfig } from "@/api/client";
import DocumentCard from "@/components/DocumentCard";
import { isAuthenticated, getSession, clearSession } from "@/lib/session";

function SkeletonGrid({ count }: { count: number }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="skeleton h-48 w-full rounded-lg" />
      ))}
    </div>
  );
}

export default function DocumentsPage() {
  const {
    data: documents,
    isLoading,
    error,
    refetch,
  } = useDocuments();

  const { data: trust } = useTrustConfig();
  const navigate = useNavigate();

  const [activeType, setActiveType] = useState<string>("all");

  const ndaRequired = trust?.tenant.nda_required ?? false;
  const hasAccess = isAuthenticated();
  const session = getSession();

  function handleSignOut() {
    clearSession();
    navigate("/");
  }

  // Collect unique document types
  const documentTypes = useMemo(() => {
    if (!documents) return [];
    const types = new Set<string>();
    for (const doc of documents) {
      types.add(doc.document_type);
    }
    return Array.from(types).sort();
  }, [documents]);

  // Filter documents by type
  const filteredDocuments = useMemo(() => {
    if (!documents) return [];
    if (activeType === "all") return documents;
    return documents.filter((d) => d.document_type === activeType);
  }, [documents, activeType]);

  function formatTypeLabel(type: string): string {
    const map: Record<string, string> = {
      soc2_report: "SOC 2",
      pen_test: "Pen Test",
      iso_cert: "ISO Cert",
      policy: "Policy",
      whitepaper: "Whitepaper",
      datasheet: "Datasheet",
      agreement: "Agreement",
      audit_report: "Audit",
    };
    return (
      map[type] ??
      type
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton h-8 w-48 rounded" />
        <div className="skeleton h-10 w-full max-w-md rounded" />
        <SkeletonGrid count={6} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-12">
        <p className="text-sm text-gray-500">Failed to load documents.</p>
        <button
          onClick={() => refetch()}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-brand-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Security Documents
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Review our security policies, audit reports, and compliance
          certifications.
        </p>
      </div>

      {/* Session banner */}
      {hasAccess && session && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-emerald-600" />
            <span className="text-sm text-emerald-700">
              Signed in as <strong>{session.name}</strong>
            </span>
          </div>
          <button
            onClick={handleSignOut}
            className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 transition-colors hover:text-emerald-800"
          >
            <LogOut className="h-3 w-3" />
            Sign out
          </button>
        </div>
      )}

      {/* NDA notice */}
      {ndaRequired && !hasAccess && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
          <div>
            <p className="text-sm font-medium text-amber-800">
              Some documents require an NDA
            </p>
            <p className="mt-0.5 text-xs text-amber-600">
              Certain documents are restricted and require a signed
              non-disclosure agreement before access is granted.{" "}
              <Link
                to="/request"
                className="font-medium underline hover:no-underline"
              >
                Request access
              </Link>
            </p>
          </div>
        </div>
      )}

      {/* Type filter tabs */}
      {documentTypes.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveType("all")}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
              activeType === "all"
                ? "bg-brand-primary text-white"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            All ({documents?.length ?? 0})
          </button>
          {documentTypes.map((type) => {
            const count =
              documents?.filter((d) => d.document_type === type).length ?? 0;
            return (
              <button
                key={type}
                onClick={() => setActiveType(type)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  activeType === type
                    ? "bg-brand-primary text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {formatTypeLabel(type)} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Documents grid */}
      {filteredDocuments.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-center">
          <FileText className="mb-3 h-10 w-10 text-gray-300" />
          <p className="text-sm text-gray-500">No documents available.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredDocuments.map((doc) => (
            <DocumentCard
              key={doc.id}
              document={doc}
              ndaRequired={ndaRequired}
              hasAccess={hasAccess}
            />
          ))}
        </div>
      )}
    </div>
  );
}
